import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { GoldRateProvider } from './context/GoldRateContext';
import { Toaster } from './components/ui/sonner';

// Pages
// Legacy pages kept for SIP testing and KYC work
import BuyPage from './pages/BuyPage';
import KYCPage from './pages/KYCPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import DownloadPage from './pages/DownloadPage';
import HomePageNew from './pages/HomePageNew';
import BookGoldPage from './pages/BookGoldPage';
import RefundGoldPageNew from './pages/RefundGoldPageNew';
import RedeemGoldPage from './pages/RedeemGoldPage';
import TransactionHistoryPage from './pages/TransactionHistoryPage';
import TermsPageNew from './pages/TermsPageNew';
import PrivacyPolicyPageNew from './pages/PrivacyPolicyPageNew';
import KYCPageNew from './pages/KYCPageNew';
import HelpPageNew from './pages/HelpPageNew';

// Layouts
import NewPagesLayout from './layouts/NewPagesLayout';

// Admin Pages
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminTransactionsPage from './pages/admin/AdminTransactionsPage';
import AdminSubAdminsPage from './pages/admin/AdminSubAdminsPage';
import AdminProfitLossPage from './pages/admin/AdminProfitLossPage';
import AdminOutstandingPage from './pages/admin/AdminOutstandingPage';
import AdminDailyReportPage from './pages/admin/AdminDailyReportPage';
import AdminVouchersPage from './pages/admin/AdminVouchersPage';
import AdminClientLedgerPage from './pages/admin/AdminClientLedgerPage';
import AdminReferralBonusPage from './pages/admin/AdminReferralBonusPage';
import AdminGoldPurchaseTrackerPage from './pages/admin/AdminGoldPurchaseTrackerPage';
import AdminNotificationCenterPage from './pages/admin/AdminNotificationCenterPage';
import AdminKYCIdentitiesPage from './pages/admin/AdminKYCIdentitiesPage';
import AdminEmployeeReferralsPage from './pages/admin/AdminEmployeeReferralsPage';
import AdminGoldRateAuditPage from './pages/admin/AdminGoldRateAuditPage';
import AdminHdfcPendingPage from './pages/admin/AdminHdfcPendingPage';
import AdminDataAuditPage from './pages/admin/AdminDataAuditPage';
import AdminSIPManagementPage from './pages/admin/AdminSIPManagementPage';
import AdminPANVerificationPage from './pages/admin/AdminPANVerificationPage';
// AdminSIPTestPage removed - SIP testing now integrated into BuyPage for admins

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="flex gap-1">
          <div className="w-3 h-3 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
          <div className="w-3 h-3 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
          <div className="w-3 h-3 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to new home page - user can login via profile drawer there
    return <Navigate to="/home-new" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Root redirects to new home */}
      <Route path="/" element={<Navigate to="/home-new" replace />} />
      {/* Public Routes */}
      <Route path="/terms" element={<Navigate to="/terms-new" replace />} />
      <Route path="/privacy-policy" element={<Navigate to="/privacy-new" replace />} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />

      {/* New Pages with shared layout - Single layout instance for seamless navigation */}
      <Route element={<NewPagesLayout />}>
        <Route path="/home-new" element={<HomePageNew />} />
        <Route path="/book-gold" element={<ProtectedRoute><BookGoldPage /></ProtectedRoute>} />
        <Route path="/refund-gold" element={<ProtectedRoute><RefundGoldPageNew /></ProtectedRoute>} />
        <Route path="/redeem-gold" element={<ProtectedRoute><RedeemGoldPage /></ProtectedRoute>} />
        <Route path="/transaction-history" element={<ProtectedRoute><TransactionHistoryPage /></ProtectedRoute>} />
        <Route path="/terms-new" element={<TermsPageNew />} />
        <Route path="/privacy-new" element={<PrivacyPolicyPageNew />} />
        <Route path="/kyc-new" element={<ProtectedRoute><KYCPageNew /></ProtectedRoute>} />
        <Route path="/help-new" element={<ProtectedRoute><HelpPageNew /></ProtectedRoute>} />
      </Route>
      
      {/* Legacy pages kept for SIP testing and KYC work */}
      <Route path="/buy" element={<ProtectedRoute><BuyPage /></ProtectedRoute>} />
      <Route path="/kyc" element={<ProtectedRoute><KYCPage /></ProtectedRoute>} />

      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute><AdminDashboardPage /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute><AdminUsersPage /></ProtectedRoute>} />
      <Route path="/admin/transactions" element={<ProtectedRoute><AdminTransactionsPage /></ProtectedRoute>} />
      <Route path="/admin/vouchers" element={<ProtectedRoute><AdminVouchersPage /></ProtectedRoute>} />
      <Route path="/admin/sub-admins" element={<ProtectedRoute><AdminSubAdminsPage /></ProtectedRoute>} />
      <Route path="/admin/profit-loss" element={<ProtectedRoute><AdminProfitLossPage /></ProtectedRoute>} />
      <Route path="/admin/outstanding" element={<ProtectedRoute><AdminOutstandingPage /></ProtectedRoute>} />
      <Route path="/admin/daily-report" element={<ProtectedRoute><AdminDailyReportPage /></ProtectedRoute>} />
      <Route path="/admin/client-ledger" element={<ProtectedRoute><AdminClientLedgerPage /></ProtectedRoute>} />
      <Route path="/admin/referral-bonus" element={<ProtectedRoute><AdminReferralBonusPage /></ProtectedRoute>} />
      <Route path="/admin/gold-purchase-tracker" element={<ProtectedRoute><AdminGoldPurchaseTrackerPage /></ProtectedRoute>} />
      <Route path="/admin/notifications" element={<ProtectedRoute><AdminNotificationCenterPage /></ProtectedRoute>} />
      <Route path="/admin/kyc-identities" element={<ProtectedRoute><AdminKYCIdentitiesPage /></ProtectedRoute>} />
      <Route path="/admin/pan-verification" element={<ProtectedRoute><AdminPANVerificationPage /></ProtectedRoute>} />
      <Route path="/admin/employee-referrals" element={<ProtectedRoute><AdminEmployeeReferralsPage /></ProtectedRoute>} />
      <Route path="/admin/gold-rate-audit" element={<ProtectedRoute><AdminGoldRateAuditPage /></ProtectedRoute>} />
      <Route path="/admin/hdfc-pending" element={<ProtectedRoute><AdminHdfcPendingPage /></ProtectedRoute>} />
      <Route path="/admin/data-audit" element={<ProtectedRoute><AdminDataAuditPage /></ProtectedRoute>} />
      <Route path="/admin/sip" element={<ProtectedRoute><AdminSIPManagementPage /></ProtectedRoute>} />
      {/* SIP testing moved to BuyPage - admin-only section */}

      {/* Public Download Page - Universal App Link */}
      <Route path="/download" element={<DownloadPage />} />

      {/* Catch all - redirect to new home */}
      <Route path="*" element={<Navigate to="/home-new" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <GoldRateProvider>
          <AppRoutes />
          <Toaster 
            position="top-center"
            toastOptions={{
              style: {
                background: '#1E3A5A',
                color: '#fff',
              },
            }}
          />
        </GoldRateProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
